import includes from 'lodash/includes'
import get from 'lodash/get'
import forOwn from 'lodash/forOwn'
import { ApolloClient, InMemoryCache, ApolloLink, split } from '@apollo/client'
import { HttpLink } from '@apollo/client/link/http'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createUploadLink } from 'apollo-upload-client'
import { getWindow, jsonTryParse, notifySocketClosed } from '@ztechapps/common.utils'
import { routesDef } from './routes'
import { appSettings } from './app-settings'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'

//when the operation name is one of the whitelisted, then it should be the http link
const hasNoUserContextOperation = ({ operationName }) => {
  const whiteListOperationName = ['login', 'logout', 'fileUpload']
  return !includes(whiteListOperationName, operationName)
}

const httpErrorLink = onError(({ graphQLErrors, response, networkError }) => {
  if (graphQLErrors) {
    let errorMessage = get(graphQLErrors, '[0].message')
    errorMessage = jsonTryParse(errorMessage)
    errorMessage = get(errorMessage, '[0]')
    const exceptionCode = get(errorMessage, 'extensions.exception.code')

    //handle the authentication error
    if (exceptionCode === 'UNAUTHORIZED') {
      //navigate to log out when there is authentication errors
      return globalThis.window.location?.replace(routesDef.LoginPage.path)
    }

    let isValidationError = false
    forOwn(errorMessage, (value, key) => {
      if (key === 'validationError') {
        isValidationError = true
      }
    })

    if (isValidationError) response.errors = errorMessage as never
  } else if (networkError) {
    const statusCode = get(networkError, 'statusCode')

    //just navigate out when authentication error
    if (statusCode === 401) {
      //navigate to log out when there is authentication errors
      return globalThis.window.location?.replace(routesDef.LoginPage.path)
    }
  }
})

const getWebSocketUri = () => {
  // Read the API URL from runtime env (window.__ENV__ via appSettings) so a single
  // built image opens sockets to the environment it actually runs in — not a value
  // baked at build time.
  const apiUrl = appSettings.AppUrl.api
  const uri = new URL(apiUrl)
  const protocol = uri.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${uri.host}/subscriptions`
}

export const setupApolloClient = () => {
  const win = getWindow()
  const uri = `/graphql/?tenantId=DFT`
  const loginToken = win.localStorage.getItem('app-login-token')

  const cache = new InMemoryCache()

  //create the http middleware
  const authMiddleware = new ApolloLink((operation, forward) => {
    // add the authorization to the headers, accessing the local storage should be here so it will be executed
    //everytime there is a need for context
    operation.setContext({
      headers: {
        'login-token': loginToken,
      },
    })
    return forward(operation)
  })

  //create the http link
  const httpLink = new HttpLink({ uri })
  const uploadLink = createUploadLink({ uri })

  //create the error link for the http only

  const transportLink = split(hasNoUserContextOperation, httpLink, uploadLink)
  const authHttpLink = ApolloLink.from([authMiddleware, httpErrorLink, transportLink])

  // Create a WebSocket link:
  const wsUri = getWebSocketUri()

  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUri,
      retryAttempts: 10,
      shouldRetry: () => true,
      on: { closed: notifySocketClosed },
      connectionParams: () => ({
        'login-token': win.localStorage.getItem('app-login-token'),
      }),
    })
  )

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    authHttpLink
  )
  return new ApolloClient({
    link,
    cache,
    ssrForceFetchDelay: 100,
  })
}
